import { createApp, h, ref, type App, type Ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'

// `ref` wraps the `App` type in a way that doesn't expose its
// private members, so when we assign an object with type `App`
// to `ref<App>()`, the types don't match. In this type we strip
// these private members to solve this type error.
type InternalApp = Omit<App<Element>, '_context' | 'config' | '_instance'>

/**
 * Composable for replacing internal Shortform links with Vue RouterLink components.
 * This ensures that internal links navigate within the app instead of opening externally.
 *
 * @param rootElementRef - A ref to the root element containing the links to replace
 * @returns An object with `replaceLinks` function and `cleanup` function
 */
export function useInternalLinkReplacement(
  rootElementRef: Ref<{ $el?: HTMLElement } | null>,
): {
  replaceLinks: () => void
  cleanup: () => void
} {
  const router = useRouter()
  const internalApps = ref<InternalApp[]>([])

  const replaceLinks = (): void => {
    // Unmount all previously created apps
    internalApps.value.forEach((app) => app.unmount())
    internalApps.value = []

    const replaceAnchor = (anchor: HTMLElement): void => {
      const url = anchor.getAttribute('href')

      if (!url) {
        return
      }

      // Skip external links
      if (
        !url.startsWith('https://www.shortform.com/app') &&
        !url.startsWith('https://dev.shortform.com/app') &&
        !url.startsWith('https://shortform.com/app') &&
        !url.startsWith('/app')
      ) {
        return
      }

      const path = url
        .replace('https://www.shortform.com', '')
        .replace('https://dev.shortform.com', '')
        .replace('https://shortform.com', '')

      // Create a new Vue app to render the RouterLink.
      // eslint-disable-next-line vue/one-component-per-file
      const linkApp = createApp({
        render() {
          return h(RouterLink, {
            to: path,
          })
        },
      })
      // Important: we need to attach the main app router to
      // the dynamic router link to have it working properly.
      linkApp.use(router)

      // Create the document fragment to mount the RouterLink.
      const el = document.createDocumentFragment()
      const html = anchor.innerHTML
      // Save all anchor attributes, we need css classes and data-xxx attribtes.
      const cssClasses = anchor.className
      const dataAttributes: { [key: string]: string } = {}
      for (let i = 0; i < anchor.attributes.length; i++) {
        const attr = anchor.attributes[i]
        if (attr.name.startsWith('data-')) {
          dataAttributes[attr.name] = attr.value
        }
      }

      // Mounting vue app to the fragment element works, but
      // type information expects string | Element, so we cast it.
      const mountedApp = linkApp.mount(el as any as Element)
      mountedApp.$el.innerHTML = html
      mountedApp.$el.className = cssClasses
      for (const key in dataAttributes) {
        mountedApp.$el.setAttribute(key, dataAttributes[key])
      }

      // Replace the original anchor with the mounted component.
      anchor.replaceWith(el)

      internalApps.value.push(linkApp)
    }

    const anchors = rootElementRef.value?.$el?.getElementsByTagName('a')
    Array.from(anchors || []).forEach((a) => replaceAnchor(a as HTMLElement))
  }

  const cleanup = (): void => {
    internalApps.value.forEach((app) => app.unmount())
    internalApps.value = []
  }

  return {
    replaceLinks,
    cleanup,
  }
}
